import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  UserIcon, 
  MagnifyingGlassIcon,
  PlusIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { patientAPI, notesAPI } from '../../api';

const PatientNotes = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    category: 'general',
    priority: 'normal'
  });

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      loadPatientNotes();
    }
  }, [selectedPatient]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await patientAPI.getPatients({ search: searchQuery });
      setPatients(response.data?.patients || []);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientNotes = async () => {
    if (!selectedPatient) return;
    
    try {
      const response = await notesAPI.getPatientNotes(selectedPatient._id);
      setNotes(response.data?.notes || []);
    } catch (error) {
      console.error('Error loading patient notes:', error);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    try {
      const noteData = {
        ...noteForm,
        patient: selectedPatient._id,
        author: 'Nurse' // Should come from auth context
      };

      if (editingNote) {
        await notesAPI.updateNote(editingNote._id, noteData);
      } else {
        await notesAPI.createNote(noteData);
      }

      setShowNoteForm(false);
      setEditingNote(null);
      setNoteForm({
        title: '',
        content: '',
        category: 'general',
        priority: 'normal'
      });
      loadPatientNotes();
      alert(editingNote ? 'Note updated successfully!' : 'Note created successfully!');
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Error saving note. Please try again.');
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title,
      content: note.content,
      category: note.category,
      priority: note.priority
    });
    setShowNoteForm(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await notesAPI.deleteNote(noteId);
      loadPatientNotes();
      alert('Note deleted successfully!');
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note. Please try again.');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'medical': return 'bg-purple-100 text-purple-800';
      case 'nursing': return 'bg-blue-100 text-blue-800';
      case 'discharge': return 'bg-green-100 text-green-800';
      case 'emergency': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const PatientCard = ({ patient }) => (
    <div 
      className={`
        p-4 rounded-lg border cursor-pointer transition-all duration-200
        ${selectedPatient?._id === patient._id 
          ? 'border-blue-500 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }
      `}
      onClick={() => setSelectedPatient(patient)}
    >
      <div className="flex items-center">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <UserIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="ml-3">
          <h4 className="font-medium text-gray-900">
            {patient.firstName} {patient.lastName}
          </h4>
          <p className="text-sm text-gray-500">ID: {patient.externalId}</p>
          <p className="text-xs text-gray-400">{patient.mobile}</p>
        </div>
      </div>
    </div>
  );

  const NoteCard = ({ note }) => (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-1">{note.title}</h4>
          <div className="flex items-center space-x-2 mb-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(note.category)}`}>
              {note.category}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(note.priority)}`}>
              {note.priority} priority
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditNote(note)}
            className="text-blue-600 hover:text-blue-700"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteNote(note._id)}
            className="text-red-600 hover:text-red-700"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <p className="text-gray-700 text-sm mb-3 whitespace-pre-wrap">{note.content}</p>
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>By: {note.author}</span>
        <div className="flex items-center">
          <ClockIcon className="w-3 h-3 mr-1" />
          {new Date(note.createdAt).toLocaleString()}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Notes</h1>
          <p className="text-gray-600">Manage patient care notes and observations</p>
        </div>
        <div className="flex items-center">
          <DocumentTextIcon className="w-8 h-8 text-blue-500" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Patient</h3>
            
            {/* Search */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Patient List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : patients.length > 0 ? (
                patients.map((patient) => (
                  <PatientCard key={patient._id} patient={patient} />
                ))
              ) : (
                <div className="text-center py-8">
                  <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No patients found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Patient Notes
                {selectedPatient && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    for {selectedPatient.firstName} {selectedPatient.lastName}
                  </span>
                )}
              </h3>
              {selectedPatient && (
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Note
                </button>
              )}
            </div>
            
            {selectedPatient ? (
              <div>
                {notes.length > 0 ? (
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <NoteCard key={note._id} note={note} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Found</h3>
                    <p className="text-gray-500">No notes have been added for this patient yet.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Patient Selected</h3>
                <p className="text-gray-500">Please select a patient from the list to view their notes.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Note Form Modal */}
      {showNoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingNote ? 'Edit Note' : 'Add New Note'}
                </h3>
                <button
                  onClick={() => {
                    setShowNoteForm(false);
                    setEditingNote(null);
                    setNoteForm({
                      title: '',
                      content: '',
                      category: 'general',
                      priority: 'normal'
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSaveNote}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      required
                      value={noteForm.title}
                      onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter note title..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={noteForm.category}
                        onChange={(e) => setNoteForm({...noteForm, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="general">General</option>
                        <option value="medical">Medical</option>
                        <option value="nursing">Nursing Care</option>
                        <option value="discharge">Discharge Planning</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={noteForm.priority}
                        onChange={(e) => setNoteForm({...noteForm, priority: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    <textarea
                      required
                      rows={6}
                      value={noteForm.content}
                      onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter detailed note content..."
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNoteForm(false);
                      setEditingNote(null);
                      setNoteForm({
                        title: '',
                        content: '',
                        category: 'general',
                        priority: 'normal'
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    {editingNote ? 'Update Note' : 'Save Note'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientNotes;